import { Component, useEffect, type ReactNode } from "react";
import { applyPageSeo } from "../lib/seo";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage />;
    }
    return this.props.children;
  }
}

export function ErrorPage() {
  useEffect(() => {
    return applyPageSeo({
      title: "Error",
      description: "An unexpected error occurred. Try refreshing the page.",
      path: "/error",
      robots: "noindex,nofollow",
    });
  }, []);

  return (
    <main className="bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.1),_transparent_38%),linear-gradient(180deg,_#f8fafc_0%,_#ffffff_50%,_#f8fafc_100%)]">
      <div className="mx-auto max-w-lg px-4 py-24 text-center sm:py-32">
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-md border border-rose-200 bg-rose-50 shadow-sm">
          <svg aria-hidden="true" className="h-8 w-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="mt-8 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
          Something went wrong
        </h1>
        <p className="mt-4 text-lg leading-8 text-slate-600">
          An unexpected error occurred. This is probably temporary — try refreshing the page.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-md bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh page
          </button>
          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
          >
            Go home
          </a>
        </div>
        <div className="mt-10 rounded-md border border-slate-200 bg-slate-50 p-5 text-left">
          <p className="text-sm font-medium text-slate-900">If this keeps happening:</p>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs text-slate-500">1</span>
              Try clearing your browser cache and refreshing
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs text-slate-500">2</span>
              Check that you're using a modern browser (Chrome, Firefox, Safari, Edge)
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-xs text-slate-500">3</span>
              If the problem persists, contact support
            </li>
          </ul>
        </div>
      </div>
    </main>
  );
}
